export interface UkPostcodeResult {
  postcode: string;
  province: string;
  city: string;
  region: string;
  raw: Record<string, unknown>;
}

const UK_NATIONS = ['England', 'Scotland', 'Wales', 'Northern Ireland'] as const;

/** Map postcodes.io fields onto our GB region dropdown options. */
function mapUkNation(r: Record<string, unknown>, postcode: string): string {
  // postcodes.io `country` is typically England | Scotland | Wales | Northern Ireland
  const country = String(r.country || '').trim();
  if ((UK_NATIONS as readonly string[]).includes(country)) return country;

  const hay = [
    r.european_electoral_region,
    r.region,
    r.admin_county,
    r.nuts,
  ]
    .map((v) => String(v || ''))
    .join(' ');
  for (const nation of UK_NATIONS) {
    if (hay.includes(nation)) return nation;
  }

  const pc = postcode.replace(/\s+/g, '').toUpperCase();
  if (pc.startsWith('BT')) return 'Northern Ireland';
  if (/^(CF|LD|LL|NP|SA)\d/.test(pc)) return 'Wales';
  // Common Scotland area codes (exclude English GL/GU)
  if (/^(EH|DD|AB|IV|KW|PH|PA|KA|ML|FK|KY|DG|TD|G)\d/.test(pc)) return 'Scotland';

  return 'England';
}

/** Free UK postcode lookup via postcodes.io (no API key). */
export async function lookupUkPostcode(postcode: string): Promise<UkPostcodeResult> {
  const cleaned = postcode.replace(/\s+/g, '').toUpperCase();
  if (!cleaned) throw new Error('Enter a postcode');

  const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(cleaned)}`);
  const data = await res.json();
  if (!res.ok || data.status !== 200 || !data.result) {
    throw new Error(data.error || 'Postcode not found');
  }
  const r = data.result as Record<string, unknown>;
  const province = mapUkNation(r, String(r.postcode || cleaned));
  const city = String(r.admin_district || r.parish || r.parliamentary_constituency || '');

  return {
    postcode: String(r.postcode || cleaned),
    province,
    city,
    region: province,
    raw: r,
  };
}
